import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map, take } from 'rxjs';

import { GbeErrorCodes } from '@gfe/core/models/gbe/gbe-error-codes.enum';
import { GbeError } from '@gfe/core/models/gbe/gbe-error.model';
import { PlanMetadataVisu } from '@gfe/core/models/plan.model';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { PlansService } from '@gfe/core/services/plans/plans.service';
import { expandToDeletionSet } from '@gfe/core/utils/plan-deletion.util';
import { isNil } from '@gfe/core/utils/shared-utils';
import {
  ModalAction,
  ModalComponent,
  ModalConfig,
  ModalType
} from '@gfe/shared/components/modal/modal.component';

/**
 * Self-contained delete-plans UX shared by every entry point (toolbox bulk action, plans
 * table row menu, graph node menu). It owns the confirmation, the deployed-plan refusal,
 * the in-flight progress modal, the backend call and the result notifications. The parent
 * only opens it and reacts to `completed`.
 */
@Component({
  selector: 'app-delete-plans-flow',
  imports: [ModalComponent, TranslatePipe],
  templateUrl: './delete-plans-flow.component.html',
  styles: ``
})
export class DeletePlansFlowComponent {
  private readonly plansService = inject(PlansService);
  private readonly notificationService = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  /** Every plan loaded for the space: descendant expansion and the guard read it. */
  plans = input<PlanMetadataVisu[]>([]);
  /** The plans the user picked. Descendants are added to them, never removed. */
  planIds = input<readonly string[]>([]);

  /** Two-way: the parent opens the flow; the flow closes itself on cancel/completion. */
  open = model<boolean>(false);

  /**
   * Emitted after every finished attempt, success or failure. A refusal leaves nothing
   * deleted, but the refusal itself usually means the loaded list was already stale, so
   * the list is worth refetching either way.
   */
  completed = output<void>();

  protected readonly isDeleting = signal(false);

  protected readonly targets = computed(() =>
    expandToDeletionSet(this.plans(), this.planIds())
  );

  /** Plans the user did not pick but that the batch must carry to be accepted. */
  protected readonly addedChildren = computed(() => {
    const picked = new Set(this.planIds());
    return this.targets().filter((plan) => !picked.has(plan.id));
  });

  /**
   * A deploy sets `isDeployed` when it *starts* and nothing ever clears it, so these
   * plans can never be deleted — not even after a deploy that failed. The whole batch is
   * blocked rather than the deployed plans dropped from it, because a deployed plan that
   * is the child of a picked one cannot be dropped at all: the batch refuses a parent
   * whose children are not all present. Blocking here saves a round trip that would take
   * up to 30 s per plan to arrive at the same refusal in worse words.
   */
  protected readonly blocked = computed(() =>
    this.targets().filter((plan) => plan.isDeployed)
  );

  protected readonly confirmModalConfig = computed<ModalConfig>(() => {
    this.currentLang();
    const cancel: ModalAction = {
      label: this.translate.instant('common.cancel'),
      callback: () => this.close(),
      color: 'cancel'
    };

    return {
      // A key of ICONS, not one of its class strings: the modal resolves it itself.
      icon: 'warning',
      type: ModalType.ERROR,
      dismissible: true,
      closeOnBackdropClick: true,
      askingModal: true,
      // No Delete button when there is nothing this call could achieve, rather than one
      // that quietly does nothing.
      actions:
        this.blocked().length > 0 || this.targets().length === 0
          ? [cancel]
          : [cancel, this.deleteAction()]
    };
  });

  // Cannot be dismissed: the operation must not be abandoned mid-delete, and a second
  // delete fired at the same plans is wasted work at best.
  protected readonly progressModalConfig: ModalConfig = {
    dismissible: false,
    closeOnBackdropClick: false
  };

  protected readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected close(): void {
    if (this.isDeleting()) {
      return;
    }
    this.open.set(false);
  }

  /** Table numbers, so the user can match the named plans to the rows on screen. */
  protected planLabels(plans: PlanMetadataVisu[]): string {
    return plans.map((plan) => `#${plan.visuId ?? plan.name}`).join(', ');
  }

  protected onConfirm(): void {
    const planIds = this.targets().map((plan) => plan.id);
    if (this.isDeleting() || this.blocked().length > 0 || planIds.length === 0) {
      return;
    }

    this.isDeleting.set(true);
    this.plansService
      .deletePlans$({ planIds })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.success(
            this.translate.instant('features.plan.delete.title'),
            this.translate.instant('features.plan.delete.success', {
              count: planIds.length
            })
          );
          this.finish();
        },
        error: (error: unknown) => {
          console.error('Error deleting plans:', error);
          this.notificationService.error(
            this.translate.instant('features.plan.delete.title'),
            this.errorMessage(error)
          );
          this.finish();
        }
      });
  }

  private deleteAction(): ModalAction {
    return {
      label: this.translate.instant('common.delete'),
      callback: () => this.onConfirm(),
      icon: 'trash',
      color: 'danger'
    };
  }

  private finish(): void {
    this.isDeleting.set(false);
    this.open.set(false);
    this.completed.emit();
  }

  /**
   * The batch is all-or-nothing, so the message can open by stating that nothing was
   * deleted, then give one line per distinct reason.
   *
   * Deliberately never renders the backend's `errorMessage`: GBE's copy for
   * `plan_already_deployed` is shared with the edit paths and reads "can't be modified",
   * which is wrong for a delete.
   */
  private errorMessage(error: unknown): string {
    const gbeError = error as GbeError | undefined;
    const keys = new Set(
      this.refusalCodes(gbeError).map((code) => this.reasonKey(code, gbeError?.status))
    );
    return ['features.plan.delete.nothing_deleted', ...keys]
      .map((key) => this.translate.instant(key))
      .join(' ');
  }

  /**
   * The code of every plan GBE refused. It judges the whole set rather than stopping at
   * the first offender, so two or more refusals arrive wrapped in a `multiple_errors`
   * envelope; reading only the top-level code would collapse the commonest real cases —
   * two optimizing plans, a stale `isDeployed` — to the generic line.
   *
   * The inner errors sit inside `details`, which `apiResponseCaseInterceptor` leaves in
   * the backend's snake_case, hence the wire spelling of `error_code`. A single offender
   * arrives as a bare refusal, with no envelope of one.
   */
  private refusalCodes(error: GbeError | undefined): string[] {
    const code = error?.errorCode ?? '';
    if (code !== GbeErrorCodes.MULTIPLE_ERRORS) {
      return [code];
    }
    const inner = (error?.details as { errors?: { error_code?: string }[] } | undefined)
      ?.errors;
    // An envelope whose items cannot be read still has to say something.
    return Array.isArray(inner) && inner.length > 0
      ? inner.map((item) => item?.error_code ?? '')
      : [code];
  }

  private reasonKey(code: string, status: number | undefined): string {
    switch (code) {
      case GbeErrorCodes.PLAN_ALREADY_DEPLOYED:
        return 'features.plan.delete.errors.plan_already_deployed';
      case GbeErrorCodes.PLAN_HAS_CHILD_PLANS:
        return 'features.plan.delete.errors.plan_has_child_plans';
      case GbeErrorCodes.PLAN_ALREADY_RUNNING:
        return 'features.plan.delete.errors.plan_already_running';
      // A plan already gone in the data layer. Reached by code rather than by status
      // when it is one refusal among several, since the envelope carries a 400.
      case GbeErrorCodes.DATA_IS_FORBIDDEN_OR_NOT_FOUND_CODE:
        return 'features.plan.delete.errors.not_found';
      default:
        break;
    }

    // The interceptor's own 403 toast is suppressed for this request, so its copy has to
    // be resolved here.
    if (status === 403) {
      return 'gbe_errors.forbidden.message';
    }
    if (status === 404) {
      return 'features.plan.delete.errors.not_found';
    }
    return 'features.plan.delete.errors.unknown';
  }
}
